import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";
import { formatDuration } from "../utils/formatDuration";
import { getOrderRoute } from "../api/client";

export default function UserTrackingMap({ order, riderLocation }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const riderMarkerRef = useRef(null);
  const routePolylineRef = useRef(null);

  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState("");

  if (!order) return null;

  const currentRiderPos = riderLocation || order.rider?.currentLocation;

  useEffect(() => {
    let cancelled = false;

    async function initMap() {
      try {
        setLoading(true);
        setMapError("");
        const google = await loadGoogleMaps();

        if (cancelled || !mapRef.current) return;

        // Center map between pickup and dropoff
        const centerLat = (order.pickup.lat + order.dropoff.lat) / 2;
        const centerLng = (order.pickup.lng + order.dropoff.lng) / 2;

        if (!mapInstanceRef.current) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: centerLat, lng: centerLng },
            zoom: 13,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });
          mapInstanceRef.current = map;

          // Add Pickup Marker
          new google.maps.Marker({
            position: { lat: order.pickup.lat, lng: order.pickup.lng },
            map,
            title: "Pickup Location",
            label: { text: "📍", fontSize: "20px" }
          });

          // Add Dropoff Marker
          new google.maps.Marker({
            position: { lat: order.dropoff.lat, lng: order.dropoff.lng },
            map,
            title: "Drop-off Location",
            label: { text: "🏁", fontSize: "20px" }
          });

          // Add Stops Markers
          order.stops?.forEach((stop, idx) => {
            new google.maps.Marker({
              position: { lat: stop.lat, lng: stop.lng },
              map,
              title: `Stop ${idx + 1}`,
              label: { text: `🛑 ${idx + 1}`, fontSize: "16px" }
            });
          });

        }

        const map = mapInstanceRef.current;

        // Add or update Rider Marker
        if (currentRiderPos?.lat && currentRiderPos?.lng) {
          if (!riderMarkerRef.current) {
            riderMarkerRef.current = new google.maps.Marker({
              position: { lat: currentRiderPos.lat, lng: currentRiderPos.lng },
              map,
              title: "Driver Location",
              label: { text: "🛵", fontSize: "24px" }
            });
          } else {
            riderMarkerRef.current.setPosition({
              lat: currentRiderPos.lat,
              lng: currentRiderPos.lng
            });
          }
        }

        // Fetch the route from our backend (Routes API — see
        // getOrderRoute in orderController.js) rather than the browser's
        // DirectionsService, which calls the legacy Directions API and is
        // blocked on this project the same way Distance Matrix was.
        try {
          const { data: route } = await getOrderRoute(
            order._id,
            currentRiderPos?.lat,
            currentRiderPos?.lng
          );
          if (cancelled) return;
          setLoading(false);

          if (route?.polyline) {
            const decodedPath = google.maps.geometry.encoding.decodePath(route.polyline);
            if (!routePolylineRef.current) {
              routePolylineRef.current = new google.maps.Polyline({
                map,
                path: decodedPath,
                strokeColor: "#00E676",
                strokeWeight: 5,
                strokeOpacity: 0.8
              });
            } else {
              routePolylineRef.current.setPath(decodedPath);
            }
            const bounds = new google.maps.LatLngBounds();
            decodedPath.forEach((p) => bounds.extend(p));
            map.fitBounds(bounds, 60);
            setRouteInfo({
              distance: route.distanceKm != null ? `${route.distanceKm} km` : "",
              duration: formatDuration(route.durationSeconds)
            });
          } else {
            setRouteInfo(null);
          }
        } catch {
          if (!cancelled) {
            setLoading(false);
            setRouteInfo(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          setMapError("Live map view initialized with real-time socket tracking.");
        }
      }
    }

    initMap();

    return () => {
      cancelled = true;
    };
  }, [
    order._id,
    order.status,
    order.pickup.lat,
    order.pickup.lng,
    order.dropoff.lat,
    order.dropoff.lng,
    currentRiderPos?.lat,
    currentRiderPos?.lng
  ]);

  // AI assistant status messages based on order state
  const getAiMessage = () => {
    switch (order.status) {
      case "requested":
        return "Connecting with closest Goboot driver in your area...";
      case "accepted":
        return `Driver assigned! ${order.rider ? order.rider.name : "Driver"} is moving towards the pickup point.`;
      case "arrived_at_pickup":
        return `Driver has arrived at pickup location (${order.pickup.address}). Preparing items for dispatch.`;
      case "picked_up":
      case "in_transit":
        return `Goods picked up! ${order.rider ? order.rider.name : "Driver"} is en route to drop-off address.`;
      case "delivered":
      case "completed":
        return "Delivery complete! Thank you for using Goboot.";
      default:
        return "Tracking order live updates.";
    }
  };

  return (
    <div className="goboot-user-tracking-card">
      {/* Goboot GPS AI Assistant Header */}
      <div className="goboot-ai-banner tracking">
        <div className="ai-banner-icon">🤖</div>
        <div className="ai-banner-text">
          <div className="ai-title">Goboot GPS AI Live Assistant</div>
          <p className="ai-msg">{getAiMessage()}</p>
        </div>
      </div>

      {/* Interactive Map */}
      <div className="map-wrapper" style={{ position: "relative", height: "320px", borderRadius: "14px", overflow: "hidden", marginBottom: "14px" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        {loading && (
          <div className="map-overlay-loading">
            <span className="spin-icon">🔄</span> Syncing GPS live route...
          </div>
        )}
      </div>

      {/* Live Route Metrics summary */}
      {routeInfo && (
        <div className="route-stats-bar">
          <div className="stat-pill">
            <span className="label">Distance to Goal</span>
            <span className="val">{routeInfo.distance}</span>
          </div>
          <div className="stat-pill">
            <span className="label">Estimated Arrival</span>
            <span className="val">{routeInfo.duration}</span>
          </div>
          <div className="stat-pill">
            <span className="label">GPS Signal</span>
            <span className="val green">● Active</span>
          </div>
        </div>
      )}
    </div>
  );
}
